import React, { useRef, useState, useEffect, useMemo, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { m } from 'framer-motion';
import classNames from 'classnames';
import CaretDown from '@/components/Icons/CaretDown';
import Check from '@/components/Icons/Check';
import Backspace from '@/components/Icons/Backspace';
import usePopover from '@/hooks/usePopover';
import useIsMobile from '@/hooks/useIsMobile';
import PlusCircle from '@components/Icons/PlusCircle';
import Button from '@components/DesignLibrary/Button';
import useQuestionnaireFormContext from '@/components/Questionnaire/QuestionnaireProvider/QuestionnaireFormProvider/useQuestionnaireFormContext';
import Buttons from '@components/Buttons';
import TrashSimple from '@components/Icons/TrashSimple';
import Spacer from '@components/Spacer';
import s from './Select.module.scss';
import Label from '../shared/Label';

export type Option = {
  key: string;
  label: string;
  description?: string;
};

const NESTED_MODAL_DISABLED_FIELDS = [
  'linkTomastercreditorlistfield53', // fieldLabel: 'For which debt?'
  'linkTomastercreditorlistfield54', // fieldLabel: 'Link a debt from the Master Creditor List'
];

const MODAL_LEVEL_THRESHOLD = 1;

export type SelectProps = {
  label: string;
  compact?: boolean;
  hideLabel?: boolean;
  description?: string;
  disabled?: boolean;
  value?: string | null;
  placeholder?: string;
  error?: string;
  optional?: boolean;
  hint?: string;
  options: Option[];
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  native?: boolean;
  ignoreKeyboardNavigation?: boolean;
  onClickAddNew?: () => void;
  canClearSelection?: boolean;
  parsedKey?: string;
  enableMultipleSelections?: boolean;
};

const MotionCaret = m(CaretDown);

const Select: React.FC<SelectProps> = ({
  label,
  parsedKey,
  compact,
  hideLabel,
  description,
  value,
  placeholder,
  disabled,
  error,
  optional,
  hint,
  options,
  onChange,
  onFocus,
  onBlur,
  native,
  ignoreKeyboardNavigation,
  onClickAddNew,
  canClearSelection = true,
  enableMultipleSelections = false,
}) => {
  const { modalLevel } = useQuestionnaireFormContext();
  const isNestedModal = modalLevel > MODAL_LEVEL_THRESHOLD;
  const disableField = disabled || (isNestedModal && NESTED_MODAL_DISABLED_FIELDS.includes(parsedKey || ''));
  const {
    isOpen,
    setIsOpen,
    menuRef,
    triggerRef,
  } = usePopover('bottom-left', true);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [checkedIndex, setCheckedIndex] = useState(-1);
  const [filter, setFilter] = useState('');
  const listRef = useRef<HTMLUListElement>(null);
  const labelId = `label-${label}`;
  const isMouseInteractionRef = useRef(false);
  const filterRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const filteredOptions = useMemo(
    () => options.filter(o =>
      String(o.label || '').toLowerCase().includes(filter.toLowerCase())
      || (o.description && String(o.description).toLowerCase().includes(filter.toLowerCase())),
    ),
    [options, filter],
  );

  // check if select triggered by mouse
  const handleMouseDown = (): void => {
    isMouseInteractionRef.current = true;
  };

  const handleFocus = (): void => {
    if (onFocus) onFocus();
    if (!isMouseInteractionRef.current) {
      setIsOpen(true);
    }
  };

  const handleClick = (): void => {
    if (isMouseInteractionRef.current) {
      setIsOpen((prev) => !prev);
    } else {
      setIsOpen(true);
    }
    isMouseInteractionRef.current = false;
  };

  useEffect(() => {
    const optionIndex = filteredOptions.findIndex(o => o.key === value);
    setSelectedIndex(optionIndex);
    setCheckedIndex(optionIndex);
  }, [value, filteredOptions]);

  useEffect(() => {
    if (disableField) {
      setIsOpen(false);
    }
  }, [disableField, setIsOpen]);

  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const optionElement = listRef.current.children[selectedIndex];
      const listRect = listRef.current.getBoundingClientRect();
      const optionRect = optionElement.getBoundingClientRect();
      if (optionRect.bottom > listRect.bottom) {
        listRef.current.scrollTop += optionRect.bottom - listRect.bottom;
      } else if (optionRect.top < listRect.top) {
        listRef.current.scrollTop -= listRect.top - optionRect.top;
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (isOpen && listRef.current && checkedIndex >= 0) {
      const optionElements = listRef.current.querySelectorAll('li');
      if (optionElements.length > checkedIndex) {
        const optionElement = optionElements[checkedIndex];
        const listRect = listRef.current.getBoundingClientRect();
        const optionRect = optionElement.getBoundingClientRect();
        if (optionRect.bottom > listRect.bottom) {
          listRef.current.scrollTop += optionRect.bottom - listRect.bottom;
        } else if (optionRect.top < listRect.top) {
          listRef.current.scrollTop -= listRect.top - optionRect.top;
        }
      }
    }
  }, [isOpen, checkedIndex, options.length]);

  const handleOptionSelect = (checkedValue: string | null): void => {
    if (checkedValue === 'addNew' && onClickAddNew) {
      onClickAddNew();
      return;
    }
    if (checkedValue === null) {
      onChange('');
      setCheckedIndex(-1);
      setTimeout(() => {
        setIsOpen(false);
      }, 150);
      return;
    }
    const option = filteredOptions.find(o => o.key === checkedValue);
    if (!option) {
      return;
    }
    const optionIndex = filteredOptions.findIndex(o => o.key === checkedValue);
    setCheckedIndex(optionIndex);
    onChange(checkedValue);
    setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const handleWrapperBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    const focusedElement = e.relatedTarget as Node;

    if (
      focusedElement !== triggerRef.current && !menuRef.current?.contains(focusedElement)
    ) {
      setIsOpen(false);
      onBlur?.();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedIndex(-1);
    } else if (options.length >= 10) {
      filterRef.current?.focus();
    }
    setFilter('');
  }, [isOpen, options.length]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!isOpen) return;

    switch (event.key) {
    case 'Escape':
      event.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();
      break;
    case 'Tab':
      event.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();
      break;
    case 'ArrowDown':
      event.preventDefault();
      event.stopPropagation();
      setSelectedIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % filteredOptions.length;
        return nextIndex;
      });
      break;
    case 'ArrowUp':
      event.preventDefault();
      event.stopPropagation();
      setSelectedIndex(prevIndex => {
        const nextIndex = (prevIndex - 1 + filteredOptions.length) % filteredOptions.length;
        return nextIndex;
      });
      break;
    case 'Enter':
      event.stopPropagation();
      if (selectedIndex > -1) {
        handleOptionSelect(filteredOptions[selectedIndex].key);
        event.preventDefault();
      }
      triggerRef.current?.focus();
      break;
    default:
      break;
    }
  };

  if (enableMultipleSelections) {
    return (
      <div className={s.content}>
        {label && <Label
          label={label}
          required={!optional}
          description={description}
          compact={compact}
          id={label}
        />}
        {(value as unknown as Array<string>)?.map((item, index) => (
          <div key={index}>
            <div className={s.dataRow}>
              <Select
                label=""
                options={options}
                value={item}
                onChange={(newValue) => (onChange as unknown as (d: string[]) => void)((value as unknown as Array<string>)
                  ?.map((v, idx) => idx === index ? newValue : v))}
                onClickAddNew={onClickAddNew}
              />
              <Buttons
                isExtraSmall
                isWrapper
                className={s.trashInput}
                onClick={() => (onChange as unknown as (d: string[]) => void)((value as unknown as Array<string>)
                  ?.filter((_, idx) => idx !== index))}
              >
                <>
                  <TrashSimple size={8} weight="fill" />
                </>
              </Buttons>
            </div>
            <Spacer size={8} />
          </div>
        ))}
        <Spacer size={8} />
        <Buttons isExtraSmall isWrapper className={s.addInput} onClick={() => (onChange as unknown as (d: string[]) => void)([...((value as unknown as Array<string>) || []), ''])}>
          <>
            <PlusCircle size={8} weight="fill" />
            Add item
          </>
        </Buttons>
      </div>
    );
  }

  if (native || isMobile) {
    return (
      <div className={s.wrapper}>
        {label && !hideLabel && (
          <Label
            label={label}
            required={!optional}
            description={description}
            compact={compact}
            id={labelId}
          />
        )}
        <select
          id={labelId}
          value={value || '#selectAnOption#'}
          onChange={e => handleOptionSelect(e.target.value === '#selectAnOption#' ? null : e.target.value)}
          className={s.nativeSelect}
          disabled={disableField}
          onFocus={onFocus}
          onBlur={onBlur}
          tabIndex={ignoreKeyboardNavigation ? -1 : 0}
        >
          <option value="#selectAnOption#" disabled={!value || !canClearSelection}>
            {(value && canClearSelection) ? '[×] Clear selection' : (placeholder || 'Select an option')}
          </option>
          {onClickAddNew && (
            <option value="addNew">
              [+] Add new
            </option>
          )}
          {options?.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={s.selectWrapper}>
      {(label || description) && !hideLabel && <Label
        label={label}
        required={!optional}
        description={description}
        compact={compact}
        id={labelId}
      />}
      <select
        className={s.hiddenNativeSelect}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        required={!optional}
        aria-hidden="true"
        tabIndex={-1}
      >
        <option disabled={disableField}></option>
        {options.map(option => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
      <div
        tabIndex={-1}
        className={s.wrapper}
        onKeyDown={handleKeyDown}
        onBlur={handleWrapperBlur}
      >
        <button
          type="button"
          className={s.button}
          onMouseDown={handleMouseDown}
          onFocus={handleFocus}
          onClick={handleClick}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-labelledby={labelId}
          disabled={disableField}
          ref={triggerRef}
          tabIndex={ignoreKeyboardNavigation ? -1 : 0}
        >
          <div className={classNames(s.trigger, !!error && s.errorState)}>
            <div className={s.triggerLabels}>
              <p
                className={classNames("body-sm", s.omitText)}
                style={
                  filteredOptions.find((o) => o.key === value)?.key
                    ? undefined
                    : { color: 'var(--color-gray-75)' }
                }
              >
                {filteredOptions.find((o) => o.key === value)?.label || placeholder || 'Select an option'}
              </p>
              {filteredOptions.find((o) => o.key === value)?.description && (
                <p className={s.description}>
                  {filteredOptions.find((o) => o.key === value)?.description}
                </p>
              )}
            </div>
            <MotionCaret
              animate={{
                rotate: isOpen ? 180 : 0,
                transition: { duration: 0.2 },
              }}
              color="var(--color-gray-75)"
              size={12}
              weight="bold"
            />
          </div>
        </button>
        {isOpen && createPortal(
          <m.div
            layout="size"
            className={s.listAndFilter}
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: 1,
              y: 4,
            }}
            ref={menuRef}
            onBlur={(e: React.FocusEvent<HTMLDivElement>) => e.stopPropagation()}
            tabIndex={-1}
            exit={{ opacity: 0, transition: { duration: 0.1 }, y: 4 }}
          >
            <div className={s.input}>
              <m.input
                layout="size"
                type="text"
                aria-label="Filter"
                placeholder="Filter..."
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
                onFocus={() => setIsOpen(true)}
                className={s.filter}
                ref={filterRef}
              />
              {onClickAddNew && (
                <Button variant='primary' size='sm' className={s.addNew} onClick={() => {
                  onClickAddNew();
                  setIsOpen(false);
                }}>
                  <PlusCircle size={16} weight='fill' color='var(--color-gray-0)' style={{ marginRight: 'var(--spacer-sm)' }} />
                  Add new
                </Button>
              )}
            </div>
            <m.ul
              initial={{ opacity: 1, y: 8 }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              role="listbox"
              className={s.list}
              ref={listRef}
              layout="size"
            >
              {value && canClearSelection && <m.li
                onMouseEnter={() => setSelectedIndex(-1)}
                role="option"
                onClick={() => handleOptionSelect(null)}
                className={s.option}
                layout="size"
              >
                <Backspace color="var(--color-gray-50)" weight="fill" />
                <div style={{ flex: 1 }}>
                  <div className='caption' style={{ color: 'var(--color-gray-75)' }}>Clear selection</div>
                </div>
              </m.li>}
              {filteredOptions?.map((option, index) => (
                <m.li
                  key={option.key + index}
                  onMouseEnter={() => setSelectedIndex(index)}
                  role="option"
                  aria-selected={selectedIndex === index}
                  aria-checked={value === option.key}
                  onClick={() => handleOptionSelect(option.key)}
                  className={s.option}
                  layout="size"
                >
                  <div className={s.triggerLabels}>
                    <p className='body-sm'>{option.label}</p>
                    {option.description && (
                      <p className={s.description}>{option.description}</p>
                    )}
                  </div>
                  {value === option.key && (
                    <Check color="var(--color-primary)" size={12} weight="bold" />
                  )}
                </m.li>
              ))}
            </m.ul>
          </m.div>,
          document.body,
        )}
      </div>
      {!hideLabel && (error || hint) && (
        <p className={error ? s.error : s.hint}>
          {error || hint}
        </p>
      )}
    </div>
  );
};

export default Select;
